import { RentalContract, MilestoneDateType } from "shared/types/rental-contracts";

/**
 * Check if a contract's indexation is URGENT (needs indexation)
 *
 * Conditions:
 * - Start date is at least 1 year in the past
 * - AND (last indexation is > 1 year ago OR never indexed)
 */
export const isIndexationUrgent = (contract: RentalContract): boolean => {
  const lastIndexation = contract.lastIndexationDate;
  const startDate = contract.contractMilestoneDates?.find(
    m => m.milestoneDateType === MilestoneDateType.START_DATE
  )?.date;

  if (!startDate) return false;

  const now = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(now.getFullYear() - 1);

  const startDateObj = new Date(startDate);
  const isStartDateOldEnough = startDateObj <= oneYearAgo;

  if (!lastIndexation) {
    // Never indexed AND start date > 1 year ago = URGENT
    return isStartDateOldEnough;
  }

  const lastIndexationObj = new Date(lastIndexation);
  const isLastIndexationOld = lastIndexationObj <= oneYearAgo;

  // Last indexation > 1 year ago AND start date > 1 year ago = URGENT
  return isLastIndexationOld && isStartDateOldEnough;
};

/**
 * Check if a contract was indexed RECENTLY (within the last year)
 *
 * Used to show green checkmark and block calculate button
 */
export const isIndexationRecent = (contract: RentalContract): boolean => {
  const lastIndexation = contract.lastIndexationDate;
  if (!lastIndexation) return false;

  const now = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(now.getFullYear() - 1);

  const lastIndexationObj = new Date(lastIndexation);
  return lastIndexationObj > oneYearAgo; // Indexed within last year
};

/**
 * Calculate indexation priority score for sorting
 *
 * Lower score = higher priority (more urgent)
 * - Never indexed = 0 (most urgent)
 * - Older indexation = lower score (more urgent)
 */
export const getIndexationPriorityScore = (contract: RentalContract): number => {
  const lastIndexation = contract.lastIndexationDate;

  if (!lastIndexation) {
    return 0; // Never indexed = highest priority
  }

  const indexationAge = Date.now() - new Date(lastIndexation).getTime();
  return indexationAge; // Older = higher priority (larger number sorts earlier when ascending)
};

/**
 * Render a Statbel correction factor at its native precision.
 *
 * Statbel publishes correction factors with up to 9 decimals
 * (e.g. 0.967996216). We render at most that many decimals and trim trailing
 * zeros so 1.0 → "1" and 0.96800 → "0.968", while a real 0.967996216 stays
 * intact.
 */
export const formatCorrectionFactor = (value: number): string => {
  const fixed = value.toFixed(9);
  if (!fixed.includes(".")) return fixed;
  return fixed.replace(/0+$/, "").replace(/\.$/, "");
};
